import React, { useEffect, useMemo, useContext, useState } from 'react';
import {
  Box,
  Collapse,
  Container,
  Grid,
  IconButton,
  Paper,
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useTranslation, useQuery } from '../../shared/hooks';
import * as translations from './intl';
import { Button, RadioButton, ConfirmationModal } from '../../components';
import { PatientForm, PatientCard } from './components';
import * as Yup from 'yup';
import { GeneralContext } from '../../shared/contexts/GeneralContext.context';
import CloseIcon from '@mui/icons-material/Close';
import {
  getLanguage,
  getPartnerId,
  getPartnerName,
  getCountryCode,
  getInstanceEnv,
} from '../../shared/constants/globalConstants';

const getStyles = (theme) => ({
  title: {
    fontFamily: 'Publico Headline !important',
    fontSize: '36px !important',
    lineHeight: '60px !important',
    color: theme.palette.text.main,
    paddingBottom: '42px',
    paddingTop: '42px',
  },
  subTitle: {
    fontFamily: 'Publico Headline',
    fontSize: '25px',
    lineHeight: '30px',
    letterSpacing: '0.8px',
    padding: '15px 50px',
    color: theme.palette.text.main,
  },
  containerPaper: {
    [theme.breakpoints.down('md')]: {
      padding: '0px 40px !important',
    },
    padding: '0px 90px !important',
    marginBottom: '20px',
  },
  paperContent: {
    width: '100%',
  },
  container: {
    marginBottom: '100px',
  },
  paddingFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '25px 0px',
    borderTop: '1px solid rgba(229, 229, 229, 1)',
  },
  paddingButton: {
    [theme.breakpoints.down('xs')]: {
      marginTop: '10px',
    },
  },
  marginButtonNew: {
    margin: '20px 0px',
  },
  editButton: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
});

const Space = styled('div')({
  width: '100%',
  height: '25px',
});

function PatientSelection() {
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = getStyles(theme);
  const query = useQuery();
  const {
    fetchClientAndDependents,
    client,
    dependents,
    refreshClientAndDependents,
    fetchAmosCountry,
    adults,
  } = useContext(GeneralContext);
  const [addNewFM, setAddNewFM] = useState(false);
  const [addNewAB, setAddNewAB] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const { locale, idiomAmos } = useTranslation({
    translationsFile: translations,
  });
  const type = query.get('type');
  const clientType = query.get('clientType') || null;
  const clientUuid = query.get('clientUuid') || null;
  const [CL, FM, AB] = ['CL', 'FM', 'AB'];
  const isVideo = useMemo(() => type === 'video', [type]);

  const {
    TITLE_VIDEO,
    TITLE_PHONE,
    TEXT,
    SUBTITLE_NEED_HELP,
    NEXT_BUTTON,
    BACK_BUTTON,
    FAMILY_MEMBER,
    PREFFIX_CLIENT,
    ADD_NEW_BUTTON,
    VALIDATION_REQUIRED,
    ADULT_BENEFICIARY,
  } = locale;

  useEffect(() => {
    fetchAmosCountry(idiomAmos);
  }, [idiomAmos]);

  useEffect(() => {
    fetchClientAndDependents();
  }, [refreshClientAndDependents]);

  const handleCLoseAddNew = () => {
    setAddNewFM(!addNewFM);
  };
  const handleCLoseAddNewAB = () => {
    setAddNewAB(!addNewAB);
  };
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleSubmit = (values) => {
    if (
      window.location.hostname === import.meta.env.VITE_DOMAIN_AMEX &&
      !isConfirmed
    ) {
      setOpenConfirm(true);
      return true;
    }
    navigate(`/request/${type}/${values.client}/${values.clientData}`);
    window.scrollTo(0, 0);

    window.dataLayer.push({
      subsidiary_name: 'axa_partners',
      business_unit: 'customer_and_solutions',
      business_line: 'travel_solutions',
      digital_business_process: 'assistance_request',
      global_digital_asset: 'doctor_please',
      partner_id: getPartnerId(),
      partner_name: getPartnerName(),
      country: getCountryCode(),
      language: getLanguage(),
      instance_environment: getInstanceEnv(),
      user_logged: 'true',
      page_name: `${type}` + ' appoinment',
      funnel_step: '2',
      step_name: 'booking appointment',
      appointment_type: `${type}` + ' call',
      event: 'pageview',
    });
  };

  const handleBack = () => {
    navigate('/');
  };

  const validationSchema = Yup.object().shape({
    client: Yup.string().required(VALIDATION_REQUIRED),
    clientData: Yup.string().required(VALIDATION_REQUIRED),
  });

  const getOptions = (client) => {
    const options = [
      {
        label: `${PREFFIX_CLIENT}${client?.name}`,
        value: CL,
      },
      { label: FAMILY_MEMBER, value: FM },
    ];
    if (
      window.location.hostname === import.meta.env.VITE_DOMAIN_TRIPUK ||
      window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC ||
      window.location.hostname === import.meta.env.VITE_DOMAIN_US ||
      window.location.hostname === import.meta.env.VITE_DOMAIN_US_CHUBB ||
      window.location.hostname === import.meta.env.VITE_DOMAIN_US_METLIFE ||
      window.location.hostname === import.meta.env.VITE_DOMAIN_USTI
    ) {
      options.push({ label: ADULT_BENEFICIARY, value: AB });
    }
    return options;
  };

  return (
    <>
      <Container maxWidth="lg" sx={classes.container}>
        <ConfirmationModal
          open={openConfirm}
          text={TEXT}
          handleAction={() => {
            setOpenConfirm(false);
            setIsConfirmed(true);
          }}
          handleClose={() => {
            setOpenConfirm(false);
            setIsConfirmed(true);
          }}
          okButton
        />
        <Grid size={{ xs: 12, md: 12 }}>
          <Box component="p" sx={classes.title}>
            {isVideo ? TITLE_VIDEO : TITLE_PHONE}
          </Box>
        </Grid>
        {client && (
          <Grid
            size={{ xs: 12, md: 12 }}
            justifyContent="center"
            className="d-flex"
          >
            <Paper sx={classes.paperContent}>
              <Box component="p" sx={classes.subTitle}>
                {SUBTITLE_NEED_HELP}
              </Box>
              <Container maxWidth="lg" sx={classes.containerPaper}>
                <Formik
                  initialValues={{
                    client: clientType ? clientType : '',
                    clientData: clientUuid ? clientUuid : '',
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue }) => (
                    <Form>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <RadioButton
                          name="client"
                          label=""
                          options={getOptions(client)}
                          onChange={(e) => {
                            setFieldValue('client', e.target.value);
                            setFieldValue('clientData', '');
                            if (
                              e.target.value === AB ||
                              e.target.value === AB
                            ) {
                              setAddNewFM(false);
                              setAddNewAB(false);
                            }
                          }}
                          key={'client'}
                        />
                      </Grid>
                      <Space />
                      <Grid size={{ xs: 12, md: 12 }}>
                        {values.client === CL && (
                          <>
                            <PatientCard payload={client} type={CL} />
                          </>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        {values.client === FM && (
                          <>
                            {dependents.map((el) => (
                              <PatientCard payload={el} key={el.id} type={FM} />
                            ))}
                          </>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        {values.client === AB && adults && (
                          <>
                            <PatientCard payload={adults} type={AB} />
                          </>
                        )}
                      </Grid>
                      {values.client === FM && (
                        <Collapse in={addNewFM}>
                          <Grid
                            size={{ xs: 12, md: 12 }}
                            sx={classes.editButton}
                          >
                            <IconButton
                              aria-label="close-add-new"
                              color="secondary"
                              onClick={handleCLoseAddNew}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Grid>
                          <Grid size={{ xs: 12, md: 12 }}>
                            <PatientForm
                              payload={{
                                relationType: '',
                                clientCode: '',
                                firstName: '',
                                lastName: '',
                                day: '',
                                month: '',
                                year: '',
                                gender: '',
                                email: '',
                                countryCode: '',
                                dependentCode: '',
                                cellphone: '',
                                dependentId: null,
                                countryResidence: '',
                                language: '',
                              }}
                              type={values.client == FM ? FM : AB}
                              handleClose={handleCLoseAddNew}
                            />
                          </Grid>
                        </Collapse>
                      )}
                      {values.client === AB && (
                        <Collapse in={addNewAB}>
                          <Grid
                            size={{ xs: 12, md: 12 }}
                            sx={classes.editButton}
                          >
                            <IconButton
                              aria-label="close-add-new"
                              color="secondary"
                              onClick={handleCLoseAddNewAB}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Grid>
                          <Grid size={{ xs: 12, md: 12 }}>
                            <PatientForm
                              payload={{
                                relationType: '',
                                clientCode: '',
                                firstName: '',
                                lastName: '',
                                day: '',
                                month: '',
                                year: '',
                                gender: '',
                                email: '',
                                countryCode: '',
                                dependentCode: '',
                                cellphone: '',
                                dependentId: null,
                                countryResidence: '',
                                language: '',
                              }}
                              type={values.client == FM ? FM : AB}
                              handleClose={handleCLoseAddNewAB}
                            />
                          </Grid>
                        </Collapse>
                      )}

                      {((values.client === FM && !addNewFM) ||
                        (values.client === AB && adults == null)) && (
                        <Grid size={{ xs: 12, md: 12 }}>
                          <Button
                            text={ADD_NEW_BUTTON}
                            sx={classes.marginButtonNew}
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              window.dataLayer.push({
                                subsidary_name: 'axa_partners',
                                business_unit: 'customer_and_solutions',
                                business_line: 'travel_solutions',
                                digital_business_process: 'assistance_request',
                                global_digital_asset: 'doctor_please',
                                partner_id: getPartnerId(),
                                partner_name: getPartnerName(),
                                country: getCountryCode(),
                                language: getLanguage(),
                                instance_environment: getInstanceEnv(),
                                user_logged: 'true',
                                page_name: 'create dependent',
                                funnel_step: '1',
                                step_name: 'request appointment',
                                'appointment_type:': `${type}` + ' call',
                                event: 'page_view',
                              });

                              if (values.client === FM) {
                                setAddNewFM(true);
                              }
                              if (values.client === AB) {
                                setAddNewAB(true);
                              }
                            }}
                          />
                        </Grid>
                      )}
                      <Grid container sx={classes.paddingFooter}>
                        <Button
                          onClick={handleBack}
                          sx={classes.paddingButton}
                          text={BACK_BUTTON}
                          variant="outlined"
                          color="primary"
                        />
                        <Button
                          type="submit"
                          sx={classes.paddingButton}
                          text={NEXT_BUTTON}
                          variant="contained"
                          color="primary"
                        />
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </Container>
            </Paper>
          </Grid>
        )}
      </Container>
    </>
  );
}

export default PatientSelection;
